import { useMemo } from 'react'
import { useApiConfig } from '../../../hooks/useApiConfig'

const MONTH_NAMES = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro']

// allowCurrent=false skryje aktuální (rozpracovaný) měsíc — pro produkty, které dávají
// smysl až nad kompletním měsícem (např. větrná růžice). Horní mez je pak předchozí
// měsíc; v lednu tím padá i horní mez roku na loňský rok.
export function MonthPeriodPicker({ year, month, onYearChange, onMonthChange, obsType, headerExtra, isAktual, onAktual, allowCurrent = true }) {
  const config = useApiConfig()

  const now = new Date()
  const nowYear = now.getFullYear()
  const nowMonth = now.getMonth() + 1

  // Nejvyšší volitelné období (aktuální měsíc, nebo předchozí když allowCurrent=false)
  const currentYear = allowCurrent || nowMonth > 1 ? nowYear : nowYear - 1
  const currentMonth = allowCurrent ? nowMonth : (nowMonth === 1 ? 12 : nowMonth - 1)

  const { minYear, minMonth } = useMemo(() => {
    const raw = config.backfillMinDate?.[obsType]
    if (!raw) return { minYear: 1900, minMonth: 1 }
    const d = new Date(raw)
    return { minYear: d.getUTCFullYear(), minMonth: d.getUTCMonth() + 1 }
  }, [config, obsType])

  const yearWindow = useMemo(() => {
    const start = year <= currentYear && year >= currentYear - 4
      ? currentYear - 4
      : year - 2
    return Array.from({ length: 5 }, (_, i) => start + i).filter(y => y >= minYear && y <= currentYear)
  }, [year, currentYear, minYear])

  const canGoBack = yearWindow[0] > minYear
  const canGoForward = yearWindow[yearWindow.length - 1] < currentYear

  function shiftWindow(delta) {
    const newStart = yearWindow[0] + delta * 5
    const clamped = Math.max(minYear, Math.min(currentYear, newStart))
    onYearChange(clamped)
  }

  const availableMonths = useMemo(() => {
    const lo = year === minYear ? minMonth : 1
    const hi = year === currentYear ? currentMonth : 12
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)
  }, [year, minYear, minMonth, currentYear, currentMonth])

  const canGoPrevMonth = !(year === minYear && month === minMonth)
  const canGoNextMonth = !(year === currentYear && month === currentMonth)

  function handlePrevMonth() {
    if (month === 1) { onYearChange(year - 1); onMonthChange(12) }
    else onMonthChange(month - 1)
  }

  function handleNextMonth() {
    if (month === 12) { onYearChange(year + 1); onMonthChange(1) }
    else onMonthChange(month + 1)
  }

  return (
    <div className="flex items-start gap-4">
      <div className="flex flex-col gap-0.5 min-w-0">
        {/* Year row */}
        <div className="flex items-center gap-1">
          <button
            className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
            onClick={() => shiftWindow(-1)}
            disabled={!canGoBack}
          >‹</button>
          {yearWindow.map(y => (
            <button
              key={y}
              onClick={() => {
                if (y === year) return
                onYearChange(y)
                if (y < year) {
                  const hi = y === currentYear ? currentMonth : 12
                  onMonthChange(hi)
                } else {
                  const lo = y === minYear ? minMonth : 1
                  onMonthChange(lo)
                }
              }}
              className={`px-3 py-px rounded text-sm font-medium transition-colors ${
                y === year ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {y}
            </button>
          ))}
          <button
            className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
            onClick={() => shiftWindow(1)}
            disabled={!canGoForward}
          >›</button>
        </div>

        {/* Month row */}
        <div className="flex items-center gap-1">
          <button
            className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
            onClick={handlePrevMonth}
            disabled={!canGoPrevMonth}
          >‹</button>
          <div className="flex flex-wrap gap-1">
            {availableMonths.map(m => (
              <button
                key={m}
                onClick={() => onMonthChange(m)}
                className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors ${
                  m === month ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {MONTH_NAMES[m - 1]}
              </button>
            ))}
          </div>
          <button
            className="px-1.5 py-0.5 text-lg leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
            onClick={handleNextMonth}
            disabled={!canGoNextMonth}
          >›</button>
          {onAktual && year === currentYear && (
            <button
              onClick={onAktual}
              className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors border whitespace-nowrap ${
                isAktual
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'text-gray-500 border-gray-300 hover:bg-gray-100'
              }`}
            >
              aktuální
            </button>
          )}
        </div>
      </div>

      {headerExtra && <div className="ml-auto flex flex-wrap items-start gap-8">{headerExtra}</div>}
    </div>
  )
}
